import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';

import Autocomplete from '@mui/material/Autocomplete';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import AdvertiserPagesService, { AdvertiserPage, AdvertiserPageGroup } from 'services/AdvertiserPagesService';
import { getActorName } from 'shared/hooks/useActorName';

// The advertiser pages an Ad Library fetch can be filtered to.
//
// ONE view, opened from both Video Creative and Static Creative — the page list
// is shared, so managing it in two places would mean two habits and two sets of
// half-populated data. Reachable from the Keywords tab toolbar in either tool,
// and from inside the page picker.
//
// Adding a page is a paste: the link goes to /v1/facebook-page-id, which fetches
// the page and reads its real id. That lookup is not optional politeness — the
// number in a profile.php URL is sometimes an alias rather than the page id
// (Rest Evercool Comforter is one), and filtering on the alias silently returns
// zero ads instead of failing.

interface AdvertiserPagesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fired after any change, so a caller showing page names can refresh. */
  onChanged?: () => void;
}

export default function AdvertiserPagesDialog({ open, onClose, onChanged }: AdvertiserPagesDialogProps) {
  const service = useMemo(() => new AdvertiserPagesService(), []);

  const [tab, setTab] = useState(0);
  const [pages, setPages] = useState<AdvertiserPage[]>([]);
  const [groups, setGroups] = useState<AdvertiserPageGroup[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupPageIds, setGroupPageIds] = useState<string[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [pageName, setPageName] = useState('');
  const [pageId, setPageId] = useState('');
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resolved, setResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // activeOnly=false — this is the management view, so a deactivated page
    // should still be visible rather than appearing to have vanished.
    const [pageResp, groupResp] = await Promise.all([service.list(false), service.listGroups(false)]);
    if (pageResp) { setPages(pageResp.pages); setError(null); } else setError('Could not load pages.');
    if (groupResp) setGroups(groupResp.groups);
    setLoading(false);
  }, [service]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const resetForm = () => {
    setUrl(''); setPageName(''); setPageId(''); setResolved(false);
  };

  const lookup = async () => {
    if (!url.trim()) return;
    setLooking(true);
    setError(null);
    setNotice(null);
    try {
      const found = await service.lookupByUrl(url.trim());
      setPageId(found.id);
      // Only fill the name if we got one and the user hasn't typed their own.
      if (found.name && !pageName.trim()) setPageName(found.name);
      setResolved(true);
      setNotice(`Found ${found.name || 'page'} — ID ${found.id}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not look that page up');
    } finally {
      setLooking(false);
    }
  };

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await service.create({
        pageId: pageId.trim(),
        pageName: pageName.trim(),
        pageUrl: url.trim() || undefined,
        createdBy: getActorName() || undefined,
      });
      resetForm();
      setNotice(null);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not add the page');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (page: AdvertiserPage) => {
    setError(null);
    try {
      await service.deactivate(page.pageId);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not remove the page');
    }
  };

  const reactivate = async (page: AdvertiserPage) => {
    setError(null);
    try {
      await service.update(page.pageId, { isActive: true });
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not restore the page');
    }
  };

  const resetGroupForm = () => { setGroupName(''); setGroupPageIds([]); setEditingGroupId(null); };

  const editGroup = (g: AdvertiserPageGroup) => {
    setEditingGroupId(g.groupId);
    setGroupName(g.groupName);
    setGroupPageIds(g.pageIds);
  };

  const saveGroup = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { groupName: groupName.trim(), pageIds: groupPageIds };
      if (editingGroupId) await service.updateGroup(editingGroupId, body);
      else await service.createGroup({ ...body, createdBy: getActorName() || undefined });
      resetGroupForm();
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save the group');
    } finally {
      setSaving(false);
    }
  };

  const removeGroup = async (g: AdvertiserPageGroup) => {
    setError(null);
    try {
      await service.deactivateGroup(g.groupId);
      if (editingGroupId === g.groupId) resetGroupForm();
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not remove the group');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Advertiser pages</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Advertisers a keyword fetch can be limited to. Shared by Video Creative and Static
          Creative — anything added here is available in both.
        </Typography>

        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Pages (${pages.length})`} />
          <Tab label={`Groups (${groups.length})`} />
        </Tabs>

        <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Add a page</Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="Facebook page link"
                placeholder="https://www.facebook.com/rest/"
                fullWidth
                value={url}
                onChange={(e) => { setUrl(e.target.value); setResolved(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') void lookup(); }}
              />
              <Button
                variant="outlined"
                startIcon={looking ? <CircularProgress size={14} /> : <SearchIcon />}
                onClick={lookup}
                disabled={looking || !url.trim()}
                sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
              >
                {looking ? 'Looking up…' : 'Look up'}
              </Button>
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                size="small" label="Page name" fullWidth
                value={pageName}
                onChange={e => setPageName(e.target.value)}
              />
              <TextField
                size="small" label="Ad Library page ID" fullWidth
                value={pageId}
                onChange={(e) => { setPageId(e.target.value); setResolved(false); }}
                helperText={resolved ? 'Looked up from the link' : 'Paste a link and press Look up, or enter it yourself'}
              />
            </Stack>

            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            <Box>
              <Button
                variant="contained"
                onClick={add}
                disabled={saving || !pageName.trim() || !pageId.trim()}
              >
                {saving ? 'Adding…' : 'Add page'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : !pages.length ? (
          <Typography variant="body2" color="text.secondary">No pages yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Page</TableCell>
                <TableCell>Ad Library ID</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map(p => (
                <TableRow key={p.pageId} hover sx={{ opacity: p.isActive ? 1 : 0.5 }}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={500}>{p.pageName}</Typography>
                      {!p.isActive && <Chip size="small" label="Removed" sx={{ height: 18, fontSize: '0.6rem' }} />}
                      {p.pageUrl && (
                        <Tooltip title={p.pageUrl}>
                          <Link href={p.pageUrl} target="_blank" rel="noreferrer" sx={{ display: 'inline-flex' }}>
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.pageId}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {p.isActive ? (
                      <Tooltip title="Remove from the picker. Keywords already using it keep working.">
                        <IconButton size="small" onClick={() => remove(p)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button size="small" onClick={() => reactivate(p)}>Restore</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        </Box>

        {/* Groups — a named set of pages, so a brand advertising from several
            pages is one thing to target. Membership is read when a scrape runs,
            so adding a page here widens every keyword already targeting the
            group; that is the whole point of a group over a one-off selection. */}
        <Box sx={{ display: tab === 1 ? 'block' : 'none' }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {editingGroupId ? 'Edit group' : 'New group'}
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small" label="Group name" fullWidth
                placeholder="Mellow Pages"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
              />
              <Autocomplete
                multiple
                options={pages.filter(p => p.isActive)}
                value={pages.filter(p => groupPageIds.includes(p.pageId))}
                getOptionLabel={p => p.pageName}
                isOptionEqualToValue={(a, b) => a.pageId === b.pageId}
                onChange={(_e, next) => setGroupPageIds(next.map(p => p.pageId))}
                renderOption={(props, p) => (
                  <li {...props} key={p.pageId}>
                    <Box>
                      <Typography variant="body2">{p.pageName}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.pageId}</Typography>
                    </Box>
                  </li>
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((p, index) => (
                    <Chip size="small" label={p.pageName} {...getTagProps({ index })} key={p.pageId} />
                  ))
                }
                renderInput={params => (
                  <TextField {...params} size="small" label="Pages in this group" />
                )}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={saveGroup}
                  disabled={saving || !groupName.trim() || !groupPageIds.length}
                >
                  {saving ? 'Saving…' : editingGroupId ? 'Save group' : 'Create group'}
                </Button>
                {editingGroupId && <Button onClick={resetGroupForm}>Cancel</Button>}
              </Stack>
            </Stack>
          </Paper>

          {!groups.length ? (
            <Typography variant="body2" color="text.secondary">No groups yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>Pages</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(g => (
                  <TableRow key={g.groupId} hover sx={{ opacity: g.isActive ? 1 : 0.5 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>{g.groupName}</Typography>
                        {!g.isActive && <Chip size="small" label="Removed" sx={{ height: 18, fontSize: '0.6rem' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {g.pageIds
                          .map(id => pages.find(p => p.pageId === id)?.pageName || id)
                          .join(', ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => editGroup(g)}>Edit</Button>
                      {g.isActive && (
                        <Tooltip title="Remove from the picker. Keywords already targeting it keep working.">
                          <IconButton size="small" onClick={() => removeGroup(g)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
